import type { KnowledgeEntry } from '../types/foreman';
import styles from './KnowledgePanel.module.css';

interface KnowledgePanelProps {
  entries: KnowledgeEntry[];
  searchQuery: string;
  onSearch: (query: string) => void;
}

const TYPE_LABELS: Record<KnowledgeEntry['type'], string> = {
  correction: 'Correction',
  practice: 'Practice',
  product_update: 'Product Update',
};

const TYPE_STYLES: Record<KnowledgeEntry['type'], string> = {
  correction: styles.typeCorrection,
  practice: styles.typePractice,
  product_update: styles.typeProductUpdate,
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function confidenceLevel(score: number): string {
  if (score >= 80) return styles.confidenceHigh;
  if (score >= 50) return styles.confidenceMedium;
  return styles.confidenceLow;
}

export function KnowledgePanel({ entries, searchQuery, onSearch }: KnowledgePanelProps) {
  const filtered = entries.filter(
    (entry) =>
      searchQuery === '' ||
      entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.source.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={styles.panel}>
      <div className={styles.searchWrap}>
        <input
          className={styles.search}
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>
      <div className={styles.list}>
        {filtered.map((entry) => (
          <div key={entry.id} className={styles.entry}>
            <div className={styles.entryHeader}>
              <span className={styles.entryTitle}>{entry.title}</span>
              <span className={`${styles.typeBadge} ${TYPE_STYLES[entry.type]}`}>
                {TYPE_LABELS[entry.type]}
              </span>
            </div>
            <div className={styles.entryDescription}>{entry.description}</div>
            <div className={styles.entryMeta}>
              {entry.confidence != null && (
                <span className={styles.confidence}>
                  <span className={styles.confidenceBar}>
                    <span
                      className={`${styles.confidenceFill} ${confidenceLevel(entry.confidence)}`}
                      style={{ width: `${entry.confidence}%` }}
                    />
                  </span>
                  {entry.confidence}%
                </span>
              )}
              <span className={styles.source}>{entry.source}</span>
              <span>{formatDate(entry.timestamp)}</span>
            </div>
            {entry.appliedTo.length > 0 && (
              <div className={styles.appliedTo}>
                {entry.appliedTo.map((skill) => (
                  <span key={skill} className={styles.skillTag}>
                    {skill}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className={styles.empty}>
            {searchQuery ? 'No matching knowledge entries' : 'No knowledge entries yet'}
          </div>
        )}
      </div>
    </div>
  );
}
